import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { AxiosError } from 'axios';
import { wrapIntoObservable } from '../shared/utils/rxjs/rxjs';

export enum ActionStatus {
  INIT,
  IN_PROGRESS,
  ABORTED,
  COMPLETE,
  FAILED
}

export enum ActionType {
  DOWNLOAD
}

export interface ActionData<T = unknown> {
  status: ActionStatus;
  metaData?: unknown;
  type: ActionType;
  result: T | null;
  error: AxiosError | null;
}

export class ActionHandlerService {
  private _actionsStatusSubject = new BehaviorSubject<{ [key: string]: ActionData }>({});

  private _actionCancellationSubject = new Subject<string>();

  getActionStatus<T = unknown>(actionId: string): Observable<ActionData<T>> {
    return this._actionsStatusSubject.pipe(
      map(actionsStatus => actionsStatus[actionId] as ActionData<T>),
      distinctUntilChanged()
    );
  }

  performAction<T>(
    actionId: string,
    actionType: ActionType,
    metaData: unknown | undefined,
    action$: Promise<T>
  ): void {
    const progressStatus: ActionData<T> = {
      status: ActionStatus.IN_PROGRESS,
      type: actionType,
      metaData,
      result: null,
      error: null
    };

    this._actionsStatusSubject.next({
      ...this._actionsStatusSubject.value,
      [actionId]: progressStatus
    });

    wrapIntoObservable(action$)
      .pipe(takeUntil(this._actionCancellationSubject))
      .subscribe({
        next: res => {
          this._actionsStatusSubject.next({
            ...this._actionsStatusSubject.value,
            [actionId]: {
              ...this._actionsStatusSubject.value[actionId],
              status: ActionStatus.COMPLETE,
              result: res,
              error: null
            }
          });
        },
        error: (error: AxiosError) => {
          this._actionsStatusSubject.next({
            ...this._actionsStatusSubject.value,
            [actionId]: {
              ...this._actionsStatusSubject.value[actionId],
              status: ActionStatus.FAILED,
              result: null,
              error
            }
          });
        }
      });
  }

  cancelAction(actionId: string): void {
    this._actionCancellationSubject.next(actionId);
    this._actionsStatusSubject.next({
      ...this._actionsStatusSubject.value,
      [actionId]: {
        ...this._actionsStatusSubject.value[actionId],
        status: ActionStatus.ABORTED,
        result: null,
        error: null
      }
    });
  }

  removeActionTrace(actionId: string): void {
    const actions = { ...this._actionsStatusSubject.value };
    delete actions[actionId];

    this._actionsStatusSubject.next(actions);
  }
}

export const actionsHandlerService = new ActionHandlerService();
